import React from 'react';
import { cn } from '@/lib/utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading = false, disabled, children, className, type = 'button', ...props }, ref) => {
    const baseClasses =
      'inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]';

    const variantClasses = {
      primary: 'bg-primary-600 text-white hover:bg-primary-700 shadow-soft',
      secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200',
      outline: 'border-2 border-primary-200 text-primary-600 bg-white hover:bg-primary-50',
      ghost: 'text-gray-600 bg-transparent hover:bg-gray-100',
      danger: 'bg-error text-white hover:bg-red-600 shadow-soft',
    };

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm min-h-[32px] gap-1.5',
      md: 'px-5 py-2.5 text-sm min-h-[40px] gap-2',
      lg: 'px-6 py-3 text-base min-h-[48px] gap-2',
    };

    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
        aria-disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {loading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Загрузка...
          </>
        ) : children}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;
